(() => {
  const parseJson = (root, selector, fallback = []) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      const value = JSON.parse(node.textContent || '');
      return value ?? fallback;
    } catch (error) {
      console.warn('[Bonvent Builds] Invalid JSON data', error);
      return fallback;
    }
  };

  const normalizeCodeList = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || '').trim().toUpperCase())
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return value
        .split(/[\s,;|]+/)
        .map((item) => item.trim().toUpperCase())
        .filter(Boolean);
    }

    return [];
  };


  const scriptLoads = new Map();

  const loadScriptOnce = (src) => {
    if (!src) return Promise.resolve();
    if (scriptLoads.has(src)) return scriptLoads.get(src);

    const promise = new Promise((resolve, reject) => {
      const existing = [...document.scripts].find((script) => script.src === src);
      if (existing) {
        if (existing.dataset.bvmLoaded === 'true') resolve();
        else {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener("error", reject, { once: true });
        }
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.bvmDynamic = 'true';
      script.addEventListener('load', () => {
        script.dataset.bvmLoaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener("error", reject, { once: true });
      document.head.appendChild(script);
    });

    scriptLoads.set(src, promise);
    return promise;
  };

  const runWhenNear = (element, callback, rootMargin = '350px 0px') => {
    if (!element || typeof callback !== 'function') return;

    if (!('IntersectionObserver' in window)) {
      const schedule = window.requestIdleCallback || ((fn) => window.setTimeout(fn, 1));
      schedule(callback);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      callback();
    }, { rootMargin });

    observer.observe(element);
  };

  const initDeferredImages = (root) => {
    root.querySelectorAll('img[data-bvm-deferred-image]').forEach((image) => {
      runWhenNear(image, () => {
        const source = image.dataset.bvmSrc;
        const sourceSet = image.dataset.bvmSrcset;
        const sizes = image.dataset.bvmSizes;
        if (sizes) image.sizes = sizes;
        if (sourceSet) image.srcset = sourceSet;
        if (source) image.src = source;
        image.removeAttribute('data-bvm-deferred-image');
        image.removeAttribute('data-bvm-src');
        image.removeAttribute('data-bvm-srcset');
        image.removeAttribute('data-bvm-sizes');
      }, '220px 0px');
    });
  };


  const calculateMarkerSizes = (count, minimumCount, maximumCount) => {
    const safeCount = Math.max(1, Number(count) || 1);
    const safeMinimum = Math.max(1, Number(minimumCount) || 1);
    const safeMaximum = Math.max(safeMinimum, Number(maximumCount) || safeMinimum);
    const minimumDesktop = 6;
    const maximumDesktop = 17;
    const minimumMobile = 5;
    const maximumMobile = 15;

    let ratio = 0;
    if (safeMaximum > safeMinimum) {
      ratio = (Math.sqrt(safeCount) - Math.sqrt(safeMinimum))
        / (Math.sqrt(safeMaximum) - Math.sqrt(safeMinimum));
    }
    ratio = Math.max(0, Math.min(1, ratio));

    return {
      desktop: Number((minimumDesktop + ((maximumDesktop - minimumDesktop) * ratio)).toFixed(2)),
      mobile: Number((minimumMobile + ((maximumMobile - minimumMobile) * ratio)).toFixed(2))
    };
  };

  const initCarousel = (track) => {
    const section = track.closest('section');
    const previous = section?.querySelector('[data-bvm-carousel-prev]');
    const next = section?.querySelector('[data-bvm-carousel-next]');
    if (!section || !previous || !next) return;

    const getStep = () => Math.max(track.clientWidth * 0.82, 280);

    const update = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft >= maxScroll - 4;
    };

    previous.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    requestAnimationFrame(update);
  };

  const initLightbox = (root) => {
    const dialog = root.querySelector('[data-bvm-hub-lightbox]');
    const image = root.querySelector('[data-bvm-hub-lightbox-image]');
    const title = root.querySelector('[data-bvm-hub-lightbox-title]');
    const caption = root.querySelector('[data-bvm-hub-lightbox-caption]');
    if (!dialog || !image || !title || !caption) return;

    let previousFocus = null;

    const close = () => {
      dialog.hidden = true;
      document.body.classList.remove('bvm-builds-lightbox-open');
      image.removeAttribute('src');
      caption.hidden = true;
      caption.textContent = '';
      previousFocus?.focus?.();
    };

    const open = (trigger) => {
      const source = trigger.dataset.bvmLightboxSrc;
      if (!source) return;

      previousFocus = trigger;
      image.src = source;
      image.alt = trigger.dataset.bvmLightboxAlt || '';
      title.textContent = trigger.dataset.bvmLightboxTitle || window.BV_T("bonvent_studio.other_shared_ui.customer_motorcycle_453206c6a9","Customer motorcycle");

      const captionText = trigger.dataset.bvmLightboxCaption || '';
      caption.textContent = captionText;
      caption.hidden = !captionText;

      dialog.hidden = false;
      document.body.classList.add('bvm-builds-lightbox-open');
      dialog.querySelector('.bvm-builds-lightbox__close')?.focus();
    };

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-bvm-lightbox-open]');
      if (trigger) {
        event.preventDefault();
        open(trigger);
        return;
      }

      if (event.target.closest('[data-bvm-hub-lightbox-close]')) {
        event.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !dialog.hidden) close();
    });
  };

  const normalizeCountryToken = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toUpperCase()
    .replace(/&/g, 'AND')
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const countryAliases = Object.freeze({
    'UNITED-STATES': 'US',
    'UNITED-STATES-OF-AMERICA': 'US',
    'UNITED-STATES-AMERICA': 'US',
    'U-S-A': 'US',
    'USA': 'US',
    'AMERICA': 'US',
    'UNITED-KINGDOM': 'GB',
    'GREAT-BRITAIN': 'GB',
    'UK': 'GB',
    'ENGLAND': 'GB',
    'SOUTH-KOREA': 'KR',
    'REPUBLIC-OF-KOREA': 'KR',
    'KOREA-SOUTH': 'KR',
    'UAE': 'AE',
    'UNITED-ARAB-EMIRATES': 'AE',
    'CZECH-REPUBLIC': 'CZ',
    'CZECHIA': 'CZ',
    'THE-NETHERLANDS': 'NL',
    'NETHERLANDS': 'NL'
  });

  const resolveCountryCoordinate = (country, coordinates) => {
    const candidates = [country?.code, country?.key, country?.name]
      .map(normalizeCountryToken)
      .filter(Boolean);

    for (const candidate of candidates) {
      const alias = countryAliases[candidate] || candidate;
      const coordinate = coordinates[alias];
      if (coordinate) return coordinate;
    }

    return null;
  };

  const mergeBuildCountries = (countries, coordinates) => {
    const merged = new Map();

    (Array.isArray(countries) ? countries : []).forEach((country) => {
      const coordinate = resolveCountryCoordinate(country, coordinates);
      if (!coordinate) return;

      const iso2 = String(coordinate.iso2 || country?.code || country?.key || '')
        .trim()
        .toUpperCase();
      if (!iso2) return;

      const count = Math.max(0, Number.parseInt(country?.count || '0', 10) || 0);
      const existing = merged.get(iso2);

      if (existing) {
        existing.count += count;
        if (!existing.name && country?.name) existing.name = String(country.name).trim();
        return;
      }

      merged.set(iso2, {
        key: iso2,
        code: iso2,
        name: String(coordinate.name || country?.name || iso2).trim(),
        count,
        coordinate
      });
    });

    return [...merged.values()].filter((country) => country.count > 0);
  };

  const initMap = async (root) => {
    const stage = root.querySelector('[data-bvm-hub-map-stage]');
    const markerLayer = root.querySelector('[data-bvm-hub-map-markers]');
    const tooltip = root.querySelector('[data-bvm-hub-map-tooltip]');
    const tooltipCountry = root.querySelector('[data-bvm-hub-map-country]');
    const tooltipCount = root.querySelector('[data-bvm-hub-map-count]');
    if (!stage || !markerLayer || !tooltip || !tooltipCountry || !tooltipCount) return;

    const coordinates = window.BVM_COUNTRY_COORDINATES || {};
    const rawBuildCountries = parseJson(root, '[data-bvm-hub-build-countries]', []);
    const buildCountries = mergeBuildCountries(rawBuildCountries, coordinates);
    const countryList = root.querySelector('[data-bvm-hub-country-list]');
    if (countryList && buildCountries.length) {
      const fragment = document.createDocumentFragment();
      buildCountries
        .slice()
        .sort((first, second) => first.name.localeCompare(second.name, undefined, { sensitivity: 'base' }))
        .forEach((country) => {
          const item = document.createElement('li');
          const count = document.createElement('span');
          item.append(document.createTextNode(`${country.name} `));
          count.textContent = String(country.count);
          item.appendChild(count);
          fragment.appendChild(item);
        });
      countryList.replaceChildren(fragment);
    }

    const salesCountries = normalizeCodeList(parseJson(root, '[data-bvm-hub-sales-countries]', []));
    const shippedIso2 = new Set();

    salesCountries.forEach((rawCode) => {
      const coordinate = resolveCountryCoordinate({ code: rawCode }, coordinates);
      const iso2 = String(coordinate?.iso2 || rawCode || '').trim().toUpperCase();
      if (iso2) shippedIso2.add(iso2);
    });

    // A published customer motorcycle is direct proof that Bonvent reached that country.
    buildCountries.forEach((country) => shippedIso2.add(country.code));

    const mapUrl = stage.dataset.mapUrl;
    const fallbackImage = stage.querySelector('[data-bvm-hub-map-fallback]');
    fallbackImage?.removeAttribute('title');
    if (mapUrl) {
      if (fallbackImage && fallbackImage.src !== mapUrl) fallbackImage.src = mapUrl;
      try {
        const response = await fetch(mapUrl, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Map request failed: ${response.status}`);
        const text = await response.text();
        const parser = new DOMParser();
        const documentSvg = parser.parseFromString(text, 'image/svg+xml');
        const svg = documentSvg.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') throw new Error("Invalid SVG map");

        svg.classList.add('bvm-builds-map__inline-map');
        svg.querySelectorAll('title').forEach((node) => node.remove());
        svg.removeAttribute('aria-labelledby');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');
        svg.removeAttribute('role');
        stage.prepend(svg);
        stage.querySelector('[data-bvm-hub-map-fallback]')?.remove();

        svg.querySelectorAll('[data-iso2], [data-iso3]').forEach((path) => {
          const iso2 = String(path.dataset.iso2 || '').trim().toUpperCase();
          const iso3 = String(path.dataset.iso3 || '').trim().toUpperCase();
          const coordinate = coordinates[iso2] || coordinates[iso3];
          const canonicalIso2 = String(coordinate?.iso2 || iso2).trim().toUpperCase();
          const isShipped = shippedIso2.has(canonicalIso2);
          path.classList.toggle('is-shipped', isShipped);
          if (isShipped) path.setAttribute('data-bvm-shipped', 'true');
          else path.removeAttribute('data-bvm-shipped');
        });
      } catch (error) {
        console.warn('[Bonvent Builds] World map enhancement unavailable', error);
      }
    }

    markerLayer.replaceChildren();
    let activeRecord = null;
    let hoveredRecord = null;

    const markerCounts = buildCountries
      .map((country) => Math.max(1, Number(country.count || 0)))
      .filter(Number.isFinite);
    const minimumMarkerCount = markerCounts.length ? Math.min(...markerCounts) : 1;
    const maximumMarkerCount = markerCounts.length ? Math.max(...markerCounts) : 1;

    const markerRecords = buildCountries
      .slice()
      .sort((first, second) => Number(second.count || 0) - Number(first.count || 0))
      .map((country) => {
        const coordinate = country.coordinate;
        if (!coordinate || !Number.isFinite(coordinate.xPercent) || !Number.isFinite(coordinate.yPercent)) return null;

        const marker = document.createElement('span');
        marker.className = 'bvm-builds-map__marker';
        marker.tabIndex = 0;
        marker.setAttribute('role', 'img');
        marker.dataset.countryCode = country.code;
        marker.dataset.buildCount = String(country.count);

        // Use a real child element for the visible dot. Older hub styles used
        // ::before with changing inset values on hover, which made the dot
        // visibly jump even after later transform overrides. A dedicated child
        // is isolated from those legacy pseudo-element rules and remains fixed.
        const markerDot = document.createElement('span');
        markerDot.className = 'bvm-builds-map__marker-dot';
        markerDot.setAttribute('aria-hidden', 'true');
        marker.appendChild(markerDot);

        const markerSizes = calculateMarkerSizes(
          country.count,
          minimumMarkerCount,
          maximumMarkerCount
        );
        marker.style.setProperty('--bvm-dot-size', `${markerSizes.desktop}px`);
        marker.style.setProperty('--bvm-dot-size-mobile', `${markerSizes.mobile}px`);
        marker.style.setProperty('--bvm-hit-size', `${Math.max(18, markerSizes.desktop + 10)}px`);
        marker.style.setProperty('--bvm-hit-size-mobile', `${Math.max(16, markerSizes.mobile + 9)}px`);
        marker.style.left = `${coordinate.xPercent}%`;
        marker.style.top = `${coordinate.yPercent}%`;
        marker.setAttribute(
          'aria-label',
          `${country.name}: ${country.count} customer build${country.count === 1 ? '' : 's'}`
        );
        markerLayer.appendChild(marker);

        return {
          country,
          coordinate,
          marker,
          x: 0,
          y: 0
        };
      })
      .filter(Boolean);

    const positionTooltip = (record) => {
      if (!record || tooltip.hidden) return;
      const stageWidth = stage.clientWidth;
      const stageHeight = stage.clientHeight;
      const tooltipWidth = tooltip.offsetWidth || 190;
      const tooltipHeight = tooltip.offsetHeight || 70;
      const margin = 10;
      const left = Math.min(
        stageWidth - (tooltipWidth / 2) - margin,
        Math.max((tooltipWidth / 2) + margin, record.x)
      );
      const showBelow = record.y - tooltipHeight - 22 < margin;
      const top = Math.min(stageHeight - margin, Math.max(margin, record.y));

      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
      tooltip.classList.toggle('is-below', showBelow);
    };

    const showTooltip = (record) => {
      if (!record) return;
      activeRecord = record;

      const count = Number(record.country.count || 0);
      tooltipCountry.textContent = record.country.name || record.coordinate.name || record.country.code;
      tooltipCount.textContent = `${count} customer build${count === 1 ? '' : 's'}`;
      tooltip.hidden = false;
      window.requestAnimationFrame(() => positionTooltip(record));
    };

    const hideTooltip = () => {
      tooltip.hidden = true;
      tooltip.classList.remove('is-below');
      activeRecord = null;
    };

    const layoutMarkers = () => {
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      if (!width || !height) return;

      markerRecords.forEach((record) => {
        record.x = (record.coordinate.xPercent / 100) * width;
        record.y = (record.coordinate.yPercent / 100) * height;
      });

      if (hoveredRecord || activeRecord) positionTooltip(hoveredRecord || activeRecord);
    };

    markerRecords.forEach((record) => {
      record.marker.addEventListener('pointerenter', () => {
        hoveredRecord = record;
        showTooltip(record);
      });
      record.marker.addEventListener('pointerleave', () => {
        hoveredRecord = null;
        if (document.activeElement !== record.marker) hideTooltip();
      });
      record.marker.addEventListener('focus', () => {
        hoveredRecord = record;
        showTooltip(record);
      });
      record.marker.addEventListener('blur', () => {
        hoveredRecord = null;
        window.setTimeout(hideTooltip, 0);
      });
      record.marker.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        // A tap focuses the button before the click event. Always keep the
        // information bubble open instead of immediately toggling it closed.
        hoveredRecord = record;
        showTooltip(record);
      });
    });

    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(layoutMarkers)
      : null;
    resizeObserver?.observe(stage);
    window.addEventListener('resize', layoutMarkers, { passive: true });
    window.requestAnimationFrame(layoutMarkers);

    document.addEventListener('click', (event) => {
      if (!stage.contains(event.target)) hideTooltip();
    });
  };

  const init = (root) => {
    if (root.dataset.bvmHubInitialized === 'true') return;
    root.dataset.bvmHubInitialized = 'true';

    initDeferredImages(root);
    initLightbox(root);

    root.querySelectorAll('[data-bvm-carousel]').forEach((track) => {
      runWhenNear(track, () => initCarousel(track), "320px 0px");
    });

    const mapStage = root.querySelector('[data-bvm-hub-map-stage]');
    if (mapStage) {
      runWhenNear(mapStage, async () => {
        if (mapStage.dataset.bvmMapInitialized === 'true') return;
        mapStage.dataset.bvmMapInitialized = 'true';
        try {
          await loadScriptOnce(mapStage.dataset.coordinatesUrl);
        } catch (error) {
          console.warn('[Bonvent Builds] Country coordinates unavailable', error);
        }
        await initMap(root);
      }, '0px');
    }
  };

  document.querySelectorAll('[data-bvm-builds-hub]').forEach(init);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll?.('[data-bvm-builds-hub]').forEach(init);
  });
})();
